import React, { useMemo } from 'react';
import { BarChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Cell, ResponsiveContainer } from 'recharts';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { calculateCgpPerGrade } from '@utils/metrics/cgpPerGrade.js';

// Same difficulty bands as the per-crag CPG chart, so colors line up across views.
const BAND_COLORS = {
  beginner: '#22c55e',
  intermediate: '#fbbf24',
  experienced: '#fb923c',
  expert: '#dc2626',
  elite: '#a855f7'
};

const bandForGrade = (grade, isBoulder) => {
  if (isBoulder) {
    if (grade < -0.5) return 'beginner';
    if (grade <= 0.5) return 'intermediate';
    if (grade <= 4) return 'experienced';
    if (grade <= 10) return 'expert';
    return 'elite';
  }
  if (grade <= 12) return 'beginner';
  if (grade <= 18) return 'intermediate';
  if (grade <= 24) return 'experienced';
  if (grade <= 32) return 'expert';
  return 'elite';
};

const CGPPerGradeChart = React.memo(({ data, climbingStyle, gradeSystem, formatGradeWithStyle }) => {
  const isBoulder = climbingStyle === CLIMBING_STYLES.BOULDER;

  const chartData = useMemo(() => {
    if (!data?.length) return [];
    const rows = calculateCgpPerGrade(data, climbingStyle);
    if (!rows.length) return [];

    // Fill gaps so the x-axis reads continuously across the grade range.
    const minGrade = rows[0].grade;
    const maxGrade = rows[rows.length - 1].grade;
    const byGrade = new Map(rows.map(r => [r.grade, r]));
    const complete = [];
    for (let g = minGrade; g <= maxGrade; g++) {
      complete.push(byGrade.get(g) || { grade: g, cgpPerDay: null, cleanCount: 0, attemptDays: 0 });
    }
    return complete;
  }, [data, climbingStyle]);

  const yDomain = useMemo(() => {
    const values = chartData.map(r => r.cgpPerDay).filter(v => typeof v === 'number');
    if (!values.length) return [0, 1];
    const min = Math.floor(Math.min(...values));
    const max = Math.ceil(Math.max(...values));
    return [min, max];
  }, [chartData]);

  if (!chartData.length) {
    return <div className="text-center py-8">No data available</div>;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={{ top: 0, right: 0, left: 0, bottom: gradeSystem === 'ewbank' ? 22 : 42 }}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="grade"
          type="category"
          tickFormatter={formatGradeWithStyle}
          interval={0}
          angle={gradeSystem === 'ewbank' ? 0 : -45}
          textAnchor={gradeSystem === 'ewbank' ? 'middle' : 'end'}
          label={{
            value: `Grade (${isBoulder ? 'V-Grade' : gradeSystem})`,
            position: 'bottom',
            offset: gradeSystem === 'ewbank' ? 8 : 28
          }}
        />
        <YAxis
          domain={yDomain}
          allowDecimals={false}
          label={{
            value: 'CGP / day at grade',
            angle: -90,
            position: 'middle',
            dx: -20
          }}
        />
        <Tooltip
          cursor={{ fill: 'transparent' }}
          labelFormatter={formatGradeWithStyle}
          formatter={(value, _name, item) => {
            const p = item?.payload;
            if (typeof value !== 'number') return ['—', 'CGP/day'];
            return [
              `${value.toFixed(2)} (${p.cleanCount} send${p.cleanCount === 1 ? '' : 's'} over ${p.attemptDays} day${p.attemptDays === 1 ? '' : 's'})`,
              'CGP/day'
            ];
          }}
        />
        <Bar
          animationDuration={300}
          dataKey="cgpPerDay"
          name="CGP/day"
        >
          {chartData.map((entry, idx) => (
            <Cell
              key={`cell-${idx}`}
              fill={BAND_COLORS[bandForGrade(entry.grade, isBoulder)]}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
});

CGPPerGradeChart.displayName = 'CGPPerGradeChart';

export default CGPPerGradeChart;
